import { Box, Card, Typography } from "@mui/material";
import grey from "@mui/material/colors/grey";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { ControlledRichTextField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-rich-text-field";
import { ControlledSelectField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-select-field";
import { ControlledTextFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-text-field";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import {
  Configuration,
  ExtendedFieldError,
  ITEM_NAMES,
  PROJECT_NAMES,
} from "vmd-types";
import { checkField, VIEW_NAMES } from "vmd-utils";

export type ReportingStructureProps = {
  configuration?: Configuration;
  language?: string;
};

export const ReportingStructurePage: PimoReactComponent<
  ReportingStructureProps,
  "update:reporting-structure",
  Configuration
> = ({ language = "en" }) => {
  const { setValue, formState } = usePimoForm<{
    configuration: Configuration;
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });
  const validationErrors = formState?.validationErrors ?? {};
  return (
    <Card
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        p: 2,
        boxShadow: "none",
        height: "calc(100vh - 83px)",
        gap: 2,
      }}
    >
      <Typography sx={{ fontSize: "24px", fontWeight: 500 }}>
        {getLocalizedMessage(language, "reportingStructure.title") ||
          "Reporting Structure"}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: grey[600],
        }}
      >
        {getLocalizedMessage(language, "reportingStructure.description") ||
          "Pimo Reporting offers tracking at three hierarchy levels. The first level is the program. The second level is the projects that are managed under the program. The third level is the tracking items that are reported in the various projects."}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 500,
            textAlign: "left",

            width: "100%",
          }}
        >
          {getLocalizedMessage(language, "reportingStructure.level1") ||
            "Level 1: Program"}
        </Typography>
        <Box
          sx={{
            p: 4,
            borderRadius: "12px",
            boxShadow: "none",
            border: "1px solid",
            borderColor: grey[300],
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, display: "flex", flexDirection: "row", gap: 1 }}>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {getLocalizedMessage(
                  language,
                  "reportingStructure.programName"
                ) || "Program Name"}
                *
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 1,
                  gap: 0,
                }}
              >
                <ControlledTextFormField
                  value={formState.configuration?.programName ?? ""}
                  onChange={(value) => {
                    setValue("configuration.programName", value);
                    const newErrors = { ...(formState.validationErrors ?? {}) };

                    checkField({
                      fieldName: "programName",
                      errors: newErrors,
                      setErrors: (errors) =>
                        setValue("validationErrors", errors),
                      value: value,
                      tabContainingErrors: VIEW_NAMES[0],
                    });

                    setValue("enableSaveButton", true);
                  }}
                  error={Boolean(validationErrors?.["programName"])}
                  helperText={validationErrors?.["programName"]?.message}
                />
              </Box>
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {getLocalizedMessage(
                  language,
                  "reportingStructure.programOwner"
                ) || "Program Owner"}
                *
              </Typography>
              <ControlledTextFormField
                value={formState.configuration?.programOwner ?? ""}
                onChange={(value) => {
                  setValue("configuration.programOwner", value);

                  const newErrors = { ...(formState.validationErrors ?? {}) };

                  checkField({
                    fieldName: "programOwner",
                    errors: newErrors,
                    setErrors: (errors) => setValue("validationErrors", errors),
                    value: value,
                    tabContainingErrors: VIEW_NAMES[0],
                  });

                  setValue("enableSaveButton", true);
                }}
                error={Boolean(validationErrors?.["programOwner"])}
                helperText={validationErrors?.["programOwner"]?.message}
              />
            </Box>
          </Box>
          <Box
            sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
              }}
            >
              {getLocalizedMessage(
                language,
                "reportingStructure.programDescription"
              ) || "Program Description"}
            </Typography>
            <ControlledRichTextField
              value={formState.configuration?.programDescription ?? ""}
              onChange={(value) => {
                setValue("configuration.programDescription", value);
                const newErrors = { ...(formState.validationErrors ?? {}) };

                checkField({
                  fieldName: "programDescription",
                  errors: newErrors,
                  setErrors: (errors) => setValue("validationErrors", errors),
                  value: value,
                  tabContainingErrors: VIEW_NAMES[0],
                });

                setValue("enableSaveButton", true);
              }}
              error={Boolean(validationErrors?.["programDescription"])}
              helperText={validationErrors?.["programDescription"]?.message}
            />
          </Box>
        </Box>

        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 500,
            textAlign: "left",
            width: "100%",
            margin: "auto",
          }}
        >
          {getLocalizedMessage(language, "reportingStructure.level2") ||
            "Level 2: Projects"}
        </Typography>
        <Box
          sx={{
            p: 4,
            borderRadius: "12px",
            boxShadow: "none",
            border: "1px solid",
            borderColor: grey[300],
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {" "}
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "14px",
            }}
          >
            {getLocalizedMessage(
              language,
              "reportingStructure.projectNameLabel"
            ) || "Name for the Projects"}
            *
          </Typography>
          <ControlledSelectField
            value={formState.configuration?.projectName ?? ""}
            onChange={(value) => {
              setValue("enableSaveButton", true);
              setValue("configuration.projectName", value);
            }}
            label={
              getLocalizedMessage(language, "formLabels.selectProjectNaming") ||
              "Please select how projects should be named"
            }
            options={PROJECT_NAMES.map((item) => ({
              label: item.single,
              value: item.single,
            }))}
          />
        </Box>
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 500,
            textAlign: "left",
            width: "100%",
            margin: "auto",
          }}
        >
          {getLocalizedMessage(language, "reportingStructure.level3") ||
            "Level 3: Tracking Items"}
        </Typography>
        <Box
          sx={{
            p: 4,
            borderRadius: "12px",
            boxShadow: "none",
            border: "1px solid",
            borderColor: grey[300],
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {" "}
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "14px",
            }}
          >
            {getLocalizedMessage(
              language,
              "reportingStructure.trackingItemNameLabel"
            ) || "Name for the Tracking Items"}
            *
          </Typography>
          <ControlledSelectField
            value={formState.configuration?.itemName ?? ""}
            onChange={(value) => {
              setValue("configuration.itemName", value);
              setValue("enableSaveButton", true);
            }}
            label={
              getLocalizedMessage(
                language,
                "formLabels.selectTrackingItemNaming"
              ) || "Please select how tracking items should be named"
            }
            options={ITEM_NAMES.map((item) => ({
              label: item.single,
              value: item.single,
            }))}
          />
        </Box>
      </Box>
    </Card>
  );
};
